import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, PawPrint, ChevronRight, Calendar, CalendarClock, AlertTriangle, CheckCircle2 } from "lucide-react";
import { computeIRIS, STAGE_LABELS } from "../lib/iris";
import { recheckStatus } from "../lib/reminders";
import { useUnits, weightDisplay, weightUnitLabel, weightToKg } from "../context/UnitsContext";

const REMINDER_TONE = {
  "on-track": { cls: "bg-success/15 text-[#3f6a52]", icon: <CheckCircle2 size={12} />, label: "On track" },
  "due-soon": { cls: "bg-warning/15 text-[#8a5a2b]", icon: <CalendarClock size={12} />, label: "Due soon" },
  due: { cls: "bg-warning/25 text-[#8a5a2b]", icon: <CalendarClock size={12} />, label: "Due" },
  overdue: { cls: "bg-destructive/15 text-destructive", icon: <AlertTriangle size={12} />, label: "Overdue" },
};

function CatCard({ cat, labs }) {
  const { units } = useUnits();
  const iris = labs ? computeIRIS(labs) : null;
  const latestLabDate = labs && labs.length
    ? [...labs].sort((a, b) => a.date.localeCompare(b.date)).slice(-1)[0].date
    : null;
  const reminder = iris?.ready ? recheckStatus(latestLabDate, iris.stage) : null;
  const tone = reminder ? REMINDER_TONE[reminder.status] : null;

  return (
    <Link
      to={`/cat/${cat.id}`}
      data-testid={`cat-card-${cat.id}`}
      className="card-soft p-6 group hover:-translate-y-0.5 transition-all duration-300 hover:border-primary/60"
    >
      <div className="flex items-start justify-between">
        <div className="w-14 h-14 rounded-2xl overflow-hidden bg-accent/15 text-accent flex items-center justify-center">
          {cat.photo ? (
            <img src={cat.photo} alt={cat.name} className="w-full h-full object-cover" />
          ) : (
            <PawPrint size={24} />
          )}
        </div>
        <ChevronRight size={18} className="text-muted-foreground group-hover:text-primary transition" />
      </div>
      <h3 className="font-display text-xl mt-5">{cat.name}</h3>
      <div className="mt-2 text-sm text-muted-foreground space-y-1">
        {cat.breed && <div>{cat.breed}</div>}
        {cat.date_of_birth && (
          <div className="inline-flex items-center gap-1.5">
            <Calendar size={13} />
            Born {cat.date_of_birth}
          </div>
        )}
        {cat.weight_kg != null && (
          <div>
            {weightDisplay(cat.weight_kg, units)} {weightUnitLabel(units)}
          </div>
        )}
      </div>

      {iris?.ready && (
        <div className="mt-4 pt-4 border-t border-border/70 space-y-2" data-testid={`cat-status-${cat.id}`}>
          <div className="text-xs font-medium text-foreground/80">
            {STAGE_LABELS[iris.stage].split("—")[0].trim()}
          </div>
          {tone && (
            <div className="flex items-center justify-between gap-2">
              <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${tone.cls}`}>
                {tone.icon} {tone.label}
              </span>
              <span className="text-xs text-muted-foreground" data-testid={`cat-due-date-${cat.id}`}>
                {reminder.status === "overdue"
                  ? `${Math.abs(reminder.daysUntilDue)}d overdue`
                  : reminder.status === "due"
                    ? "due now"
                    : `due ${reminder.dueDate}`}
              </span>
            </div>
          )}
        </div>
      )}
    </Link>
  );
}

function AddCatDialog({ onAdded }) {
  const { units } = useUnits();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    breed: "",
    sex: "",
    date_of_birth: "",
    weight_kg: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        weight_kg: form.weight_kg === "" ? null : parseFloat(form.weight_kg),
      };
      const { data } = await api.post("/cats", payload);
      toast.success(`${data.name} added`);
      setOpen(false);
      setForm({ name: "", breed: "", sex: "", date_of_birth: "", weight_kg: "", notes: "" });
      onAdded?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-cat-button" className="rounded-full">
          <Plus size={16} className="mr-1.5" /> Add a cat
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="add-cat-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Add a cat</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="cat-name">Name</Label>
            <Input
              id="cat-name"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="add-cat-name-input"
              className="mt-1.5 rounded-xl"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cat-breed">Breed</Label>
              <Input
                id="cat-breed"
                value={form.breed}
                onChange={(e) => setForm({ ...form, breed: e.target.value })}
                data-testid="add-cat-breed-input"
                className="mt-1.5 rounded-xl"
                placeholder="DSH, Ragdoll…"
              />
            </div>
            <div>
              <Label>Sex</Label>
              <Select value={form.sex} onValueChange={(v) => setForm({ ...form, sex: v })}>
                <SelectTrigger className="mt-1.5 rounded-xl" data-testid="add-cat-sex-select">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MN">Male neutered</SelectItem>
                  <SelectItem value="FS">Female spayed</SelectItem>
                  <SelectItem value="M">Male intact</SelectItem>
                  <SelectItem value="F">Female intact</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cat-dob">Date of birth</Label>
              <Input
                id="cat-dob"
                type="date"
                value={form.date_of_birth}
                onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                data-testid="add-cat-dob-input"
                className="mt-1.5 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="cat-weight">Weight ({weightUnitLabel(units)})</Label>
              <Input
                id="cat-weight"
                type="number"
                step="0.1"
                value={form.weight_kg}
                onChange={(e) => setForm({ ...form, weight_kg: e.target.value })}
                data-testid="add-cat-weight-input"
                className="mt-1.5 rounded-xl"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="cat-notes">Notes</Label>
            <Textarea
              id="cat-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="add-cat-notes-input"
              className="mt-1.5 rounded-xl"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={submitting} data-testid="add-cat-submit-button" className="rounded-full">
              {submitting ? "Saving…" : "Save cat"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Dashboard() {
  const [cats, setCats] = useState(null);
  const [labsByCat, setLabsByCat] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/cats");
      setCats(data);
      // Parallel fetch labs for each cat
      const results = await Promise.all(
        data.map((c) =>
          api
            .get(`/cats/${c.id}/labs`)
            .then((r) => [c.id, r.data])
            .catch(() => [c.id, []]),
        ),
      );
      setLabsByCat(Object.fromEntries(results));
    } catch (err) {
      toast.error(formatApiError(err));
      setCats([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <Layout>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">Your cats</h1>
          <p className="text-muted-foreground mt-2">
            Track lab results and view the IRIS stage for each of your cats.
          </p>
        </div>
        <AddCatDialog onAdded={load} />
      </div>

      <div className="mt-10">
        {cats === null ? (
          <div className="text-muted-foreground">Loading…</div>
        ) : cats.length === 0 ? (
          <div
            className="card-soft p-12 text-center max-w-lg mx-auto"
            data-testid="empty-cats-state"
          >
            <div className="w-14 h-14 rounded-full bg-primary/10 text-primary mx-auto flex items-center justify-center">
              <PawPrint size={24} />
            </div>
            <h3 className="font-display text-xl mt-4">No cats yet</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Add your first cat to start logging lab results and IRIS staging.
            </p>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            data-testid="cats-grid"
          >
            {cats.map((c) => (
              <CatCard key={c.id} cat={c} labs={labsByCat[c.id]} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
